/**
 * roles.js — Shared role definitions for ZeusCMS
 * Import from any page to get consistent labels, colors, and access checks.
 */

export const ROLE_LABELS = {
  // Current roles
  owner:             'Owner',
  administrator:     'Administrator',
  managing_attorney: 'Managing Attorney / Partner',
  attorney:          'Attorney',
  paralegal:         'Paralegal',
  case_manager:      'Case Manager',
  intake_specialist: 'Intake Specialist',
  billing:           'Billing / Accounting',
  medical_records:   'Medical Records Specialist',
  litigation_staff:  'Litigation Staff',
  reporter:          'Reporter / Read Only',
  client_portal:     'Client Portal User',
  guest:             'Guest / Co-Counsel',
  // Legacy (keep for users already in DB)
  admin:             'Admin',
  staff:             'Staff',
};

// Roles available in invite/edit dropdowns (owner is excluded — set on org creation)
export const ASSIGNABLE_ROLES = [
  'administrator',
  'managing_attorney',
  'attorney',
  'paralegal',
  'case_manager',
  'intake_specialist',
  'billing',
  'medical_records',
  'litigation_staff',
  'reporter',
  'client_portal',
  'guest',
];

// Short description shown under each role in the invite form
export const ROLE_DESCRIPTIONS = {
  administrator:     'Full access — manage team, settings, and all cases',
  managing_attorney: 'Senior attorney with billing and team oversight',
  attorney:          'Handles cases, documents, and client communication',
  paralegal:         'Supports attorneys; case data, documents, notes',
  case_manager:      'Day-to-day case tracking and coordination',
  intake_specialist: 'New client intake, lead qualification',
  billing:           'Invoices, payments, and financial records',
  medical_records:   'Treatment records, providers, and medical docs',
  litigation_staff:  'Hearing prep, court filings, depositions',
  reporter:          'Read-only access — view cases and reports only',
  client_portal:     'Limited access for client self-service portal',
  guest:             'External co-counsel or temporary collaborator',
};

// Role color badges
export const ROLE_COLORS = {
  owner:             { bg: 'rgba(212,168,83,0.15)',  color: '#d4a853' },
  administrator:     { bg: 'rgba(239,68,68,0.12)',   color: '#ef4444' },
  managing_attorney: { bg: 'rgba(168,85,247,0.12)',  color: '#a855f7' },
  attorney:          { bg: 'rgba(59,130,246,0.12)',  color: '#3b82f6' },
  paralegal:         { bg: 'rgba(14,165,233,0.12)',  color: '#0ea5e9' },
  case_manager:      { bg: 'rgba(34,197,94,0.12)',   color: '#22c55e' },
  intake_specialist: { bg: 'rgba(20,184,166,0.12)',  color: '#14b8a6' },
  billing:           { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b' },
  medical_records:   { bg: 'rgba(236,72,153,0.12)',  color: '#ec4899' },
  litigation_staff:  { bg: 'rgba(249,115,22,0.12)',  color: '#f97316' },
  reporter:          { bg: 'rgba(100,116,139,0.12)', color: '#64748b' },
  client_portal:     { bg: 'rgba(100,116,139,0.10)', color: '#94a3b8' },
  guest:             { bg: 'rgba(100,116,139,0.08)', color: '#94a3b8' },
  // Legacy
  admin:             { bg: 'rgba(239,68,68,0.12)',   color: '#ef4444' },
  staff:             { bg: 'rgba(100,116,139,0.12)', color: '#64748b' },
};

// Roles that can be assigned as the attorney on a case
export const ATTORNEY_ROLES = [
  'owner', 'administrator', 'managing_attorney', 'attorney',
  'admin', // legacy
];

// Default access level per role
export const ROLE_DEFAULT_ACCESS = {
  owner:             'super_admin',
  administrator:     'super_admin',
  managing_attorney: 'admin',
  attorney:          'admin',
  paralegal:         'standard',
  case_manager:      'standard',
  intake_specialist: 'standard',
  billing:           'standard',
  medical_records:   'standard',
  litigation_staff:  'standard',
  reporter:          'standard',
  client_portal:     'standard',
  guest:             'standard',
  admin:             'admin',
  staff:             'standard',
};

/** Returns a role's display label, falling back to the raw slug */
export const getRoleLabel = (role) => ROLE_LABELS[role] || role || '—';

/** Returns badge style for a role */
export const getRoleBadgeStyle = (role) => {
  const c = ROLE_COLORS[role] || { bg: 'rgba(100,116,139,0.1)', color: '#64748b' };
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '0.72rem',
    fontWeight: 700,
    background: c.bg,
    color: c.color,
  };
};
